import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';

export interface SubClinic {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  address: Json;
  type: 'psychologist' | 'dentist' | 'specialist' | 'emdr_therapist';
  cvr: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  patient_count: number;
  appointment_count: number;
}

/**
 * Fetches all sub-clinics for a parent clinic via list_sub_clinics RPC
 */
export function useSubClinics(parentClinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['sub-clinics', parentClinicId],
    enabled: Boolean(parentClinicId),
    queryFn: async (): Promise<SubClinic[]> => {
      if (!parentClinicId) return [];
      const { data, error } = await supabase.rpc('list_sub_clinics', {
        p_parent_clinic_id: parentClinicId,
      });
      if (error) throw error;
      return (data ?? []) as SubClinic[];
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Activates multi-clinic module for a clinic
 */
export function useActivateMultiClinic(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!clinicId) throw new Error('No active clinic');
      
      // Use toggle_clinic_module RPC which properly handles activated_modules
      const { data, error } = await supabase.rpc('toggle_clinic_module', {
        p_clinic_id: clinicId,
        p_module_key: 'multiClinic',
        p_enabled: true,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Update clinic query cache with returned data
      queryClient.setQueryData(['clinic', clinicId], data);
    },
  });
}

/**
 * Fetches a single sub-clinic by ID
 */
export function useSubClinic(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['sub-clinic', clinicId],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) throw new Error('No clinic ID provided');
      
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', clinicId)
        .single();
      
      if (error) throw error;
      return data;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Updates a sub-clinic
 */
export function useUpdateSubClinic() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: {
      clinicId: string;
      name?: string;
      email?: string;
      phone?: string | null;
      address?: Json;
      cvr?: string | null;
    }) => {
      const { clinicId, ...updates } = params;
      
      const { data, error } = await supabase.rpc('update_sub_clinic', {
        p_clinic_id: clinicId,
        p_name: updates.name,
        p_email: updates.email,
        p_phone: updates.phone ?? undefined,
        p_address: updates.address,
        p_cvr: updates.cvr ?? undefined,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { clinicId }) => {
      queryClient.invalidateQueries({ queryKey: ['sub-clinic', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['sub-clinics'] });
    },
  });
}

/**
 * Toggles sub-clinic active status
 */
export function useToggleSubClinicStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { clinicId: string; active: boolean }) => {
      const { data, error } = await supabase.rpc('toggle_sub_clinic_status', {
        p_clinic_id: params.clinicId,
        p_active: params.active,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { clinicId }) => {
      queryClient.invalidateQueries({ queryKey: ['sub-clinic', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['sub-clinics'] });
    },
  });
}

/**
 * Deletes a sub-clinic
 */
export function useDeleteSubClinic() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (clinicId: string) => {
      const { data, error } = await supabase.rpc('delete_sub_clinic', {
        p_clinic_id: clinicId,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-clinics'] });
    },
  });
}
